/**
 * @typedef {import('../../contracts/forms.js').FieldDef} FieldDef
 * @typedef {import('../../contracts/forms.js').FieldRegistry} FieldRegistry
 * @typedef {import('../../contracts/forms.js').FieldDriverHandle} FieldDriverHandle
 * @typedef {import('../../contracts/forms.js').OptionsProviderHandle} OptionsProviderHandle
 */

import { createElement } from '../../core/dom/helpers.js';
import { createLogger } from '../../core/logging/logger.js';

const log = createLogger({ module: 'form-field' });

/**
 * @typedef {Object} FormFieldOptions
 * @property {FieldDef} def
 * @property {unknown} [value]
 * @property {(value: unknown) => void} onChange
 * @property {FieldRegistry} [registry]
 * @property {() => Record<string, unknown>} [getFormValues] - Returns a snapshot of current form values. Forwarded to driver ctx.
 * @property {import('../../contracts/upload.js').UploadClient} [uploadClient] - Forwarded to driver ctx.
 * @property {import('../../contracts/media.js').MediaClient} [mediaClient] - Forwarded to driver ctx for media browsing.
 * @property {import('../../contracts/forms.js').FilePicker} [filePicker] - Forwarded to driver ctx (file-field library-picker capability: browse + modal open).
 * @property {import('../../contracts/forms.js').FormPicker} [formPicker] - Forwarded to driver ctx (form-link create/copy-from modal opener).
 * @property {import('../../contracts/locale.js').LocaleConfig} [localeConfig] - Forwarded to driver ctx for locale-aware drivers (date/datetime/time).
 */

/**
 * @typedef {Object} FormFieldInstance
 * @property {(value: unknown) => void} setValue
 * @property {(errors: string[]) => void} setErrors
 * @property {() => unknown} getValue
 * @property {() => void} destroy
 * @property {(visible: boolean) => void} setVisible - Toggles wrapper visibility. Does not clear values.
 * @property {(disabled: boolean) => void} setDisabled - Forwards to driver handle if present.
 * @property {() => void} clearValue - Silent host-driven reset. Does not emit onChange.
 * @property {() => OptionsProviderHandle | undefined} getOptionsProvider - Exposed by option-based drivers.
 * @property {() => HTMLElement} getWrapper - Returns the outer wrapper element.
 */

/**
 * Validates that a driver handle has all required functions.
 * @param {unknown} handle
 * @returns {handle is FieldDriverHandle}
 */
function isValidHandle(handle) {
  if (!handle || typeof handle !== 'object') return false;
  const h = /** @type {Record<string, unknown>} */ (handle);
  return (
    typeof h.getValue === 'function' &&
    typeof h.setValue === 'function' &&
    typeof h.setErrors === 'function' &&
    typeof h.destroy === 'function'
  );
}

/**
 * Creates a dumb form field component.
 *
 * @param {HTMLElement} container
 * @param {FormFieldOptions} options
 * @returns {FormFieldInstance}
 */
export function createFormField(container, options) {
  const { def, onChange, registry } = options;

  const wrapper = createElement('div', { class: 'form-field' });
  const label = createElement('label', { for: `field-${def.key}`, class: 'form-field-label' }, [def.label]);
  wrapper.appendChild(label);

  const inputContainer = createElement('div', { class: 'form-field-input-wrap' });
  wrapper.appendChild(inputContainer);

  const errorsDiv = createElement('div', { class: 'form-field-errors' });
  wrapper.appendChild(errorsDiv);

  const fieldInfoDef = /** @type {import('../../contracts/forms.js').FieldDefBase} */ (/** @type {unknown} */ (def));
  if (fieldInfoDef.fieldInfo) {
    const infoEl = createElement('small', { class: 'form-field-info' }, [fieldInfoDef.fieldInfo]);
    wrapper.appendChild(infoEl);
  }

  container.appendChild(wrapper);

  /** @type {FieldDriverHandle | null} */
  let driverHandle = null;

  // --- Registry path ---
  if (registry) {
    const driver = registry.get(def.type);
    if (driver) {
      const ctx = {
        def,
        value: options.value,
        onChange,
        setErrors,
        getFormValues: options.getFormValues,
        uploadClient: options.uploadClient,
        mediaClient: options.mediaClient,
        filePicker: options.filePicker,
        formPicker: options.formPicker,
        localeConfig: options.localeConfig,
      };
      let handle;
      try {
        handle = driver.mount(inputContainer, ctx);
      } catch {
        // mount threw — render placeholder
      }

      if (handle && isValidHandle(handle)) {
        driverHandle = handle;
      } else {
        // Partial mount cleanup
        if (handle && typeof /** @type {Record<string,unknown>} */ (handle).destroy === 'function') {
          try { /** @type {FieldDriverHandle} */ (handle).destroy(); } catch { /* ignore */ }
        }
        inputContainer.innerHTML = '';
        renderUnsupportedPlaceholder(`Driver error: '${def.type}'`);
      }
    } else {
      renderUnsupportedPlaceholder(`Unsupported field type: '${def.type}'`);
    }

    // Hidden fields carry their value silently — suppress all visible chrome.
    if (def.type === 'hidden') {
      wrapper.hidden = true;
    }
  }

  // --- Fallback path (no registry) ---
  /** @type {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null} */
  let inputEl = null;

  if (!registry) {
    if (def.type === 'textarea') {
      inputEl = /** @type {HTMLTextAreaElement} */ (createElement('textarea', {
        id: `field-${def.key}`,
        name: def.key,
        class: 'form-field-input',
      }));
      if (def.placeholder) inputEl.setAttribute('placeholder', def.placeholder);
      if (def.required) { inputEl.setAttribute('required', 'required'); inputEl.setAttribute('aria-required', 'true'); }
      if (def.disabled) inputEl.setAttribute('disabled', 'disabled');
      const textareaRef = inputEl;
      inputEl.addEventListener('input', () => onChange(textareaRef.value));
    } else if (def.type === 'select') {
      inputEl = /** @type {HTMLSelectElement} */ (createElement('select', {
        id: `field-${def.key}`,
        name: def.key,
        class: 'form-field-input',
      }));
      inputEl.appendChild(createElement('option', { value: '' }, [def.placeholder ?? 'Select...']));
      for (const opt of def.options ?? []) {
        inputEl.appendChild(createElement('option', { value: opt.value }, [opt.label]));
      }
      if (def.required) { inputEl.setAttribute('required', 'required'); inputEl.setAttribute('aria-required', 'true'); }
      if (def.disabled) inputEl.setAttribute('disabled', 'disabled');
      const selectRef = inputEl;
      inputEl.addEventListener('change', () => onChange(selectRef.value));
    } else if (def.type === 'checkbox') {
      inputEl = /** @type {HTMLInputElement} */ (createElement('input', {
        type: 'checkbox',
        id: `field-${def.key}`,
        name: def.key,
        class: 'form-field-checkbox',
      }));
      if (def.disabled) inputEl.setAttribute('disabled', 'disabled');
      const cbRef = /** @type {HTMLInputElement} */ (inputEl);
      inputEl.addEventListener('change', () => {
        onChange(cbRef.checked);
      });
    } else {
      // text or date
      inputEl = /** @type {HTMLInputElement} */ (createElement('input', {
        type: def.type === 'date' ? 'date' : 'text',
        id: `field-${def.key}`,
        name: def.key,
        class: 'form-field-input',
      }));
      if (def.placeholder) inputEl.setAttribute('placeholder', def.placeholder);
      if (def.required) { inputEl.setAttribute('required', 'required'); inputEl.setAttribute('aria-required', 'true'); }
      if (def.disabled) inputEl.setAttribute('disabled', 'disabled');
      const inputRef = inputEl;
      inputEl.addEventListener('input', () => onChange(inputRef.value));
    }

    inputContainer.appendChild(inputEl);

    // Set initial value
    if (options.value !== undefined) {
      setValueFallback(options.value);
    }
  }

  /**
   * @param {string} message
   */
  function renderUnsupportedPlaceholder(message) {
    const placeholder = createElement('div', { class: 'form-field-unsupported' }, [message]);
    inputContainer.appendChild(placeholder);
  }

  /**
   * @param {unknown} value
   */
  function setValueFallback(value) {
    if (!inputEl) return;
    if (def.type === 'checkbox') {
      /** @type {HTMLInputElement} */ (inputEl).checked = Boolean(value);
    } else {
      inputEl.value = value == null ? '' : String(value);
    }
  }

  /**
   * @param {unknown} value
   */
  function setValue(value) {
    if (driverHandle) {
      driverHandle.setValue(value);
    } else {
      setValueFallback(value);
    }
  }

  /**
   * @param {string[]} errors
   */
  function setErrors(errors) {
    errorsDiv.textContent = '';
    const hasErrors = errors.length > 0;
    if (hasErrors) {
      wrapper.classList.add('form-field--error');
      for (const msg of errors) {
        const errorEl = createElement('div', { class: 'form-field-error' }, [msg]);
        errorsDiv.appendChild(errorEl);
      }
    } else {
      wrapper.classList.remove('form-field--error');
    }
    // Propagate aria-invalid to driver (drivers that implement setAriaInvalid)
    if (driverHandle?.setAriaInvalid) {
      driverHandle.setAriaInvalid(hasErrors);
    } else if (inputEl) {
      // Fallback path: set aria-invalid directly on the native input element
      inputEl.setAttribute('aria-invalid', hasErrors ? 'true' : 'false');
    }
  }

  /**
   * @returns {unknown}
   */
  function getValue() {
    if (driverHandle) {
      return driverHandle.getValue();
    }
    if (!inputEl) return undefined;
    if (def.type === 'checkbox') {
      return /** @type {HTMLInputElement} */ (inputEl).checked;
    }
    return inputEl.value;
  }

  function destroy() {
    if (driverHandle) {
      driverHandle.destroy();
    }
    wrapper.remove();
  }

  /**
   * Toggles wrapper visibility. Does not clear or disable values.
   * All clear/disable decisions are made in the host (crud-editor).
   * @param {boolean} visible
   */
  function setVisible(visible) {
    wrapper.hidden = !visible;
    driverHandle?.setVisible?.(visible);
  }

  /**
   * Forwards disabled state to driver handle.
   * @param {boolean} disabled
   */
  function setDisabled(disabled) {
    driverHandle?.setDisabled?.(disabled);
  }

  /**
   * Silent host-driven reset. Does not emit onChange.
   * Every driver used by crud-editor must implement clearValue() on the handle.
   * Falls back to type-appropriate empty state for built-in fallback path.
   */
  function clearValue() {
    if (driverHandle) {
      if (typeof driverHandle.clearValue === 'function') {
        driverHandle.clearValue();
      } else {
        log.warn('clearValue() not implemented on driver handle', { data: { type: def.type } });
      }
      return;
    }
    // Fallback path (no registry): best-effort by type
    if (!inputEl) return;
    if (def.type === 'checkbox') {
      /** @type {HTMLInputElement} */ (inputEl).checked = false;
    } else {
      inputEl.value = '';
    }
  }

  /**
   * Returns the driver's options provider handle, if any.
   * @returns {OptionsProviderHandle | undefined}
   */
  function getOptionsProvider() {
    return driverHandle?.getOptionsProvider?.();
  }

  /**
   * Returns the outer wrapper element.
   * @returns {HTMLElement}
   */
  function getWrapper() {
    return wrapper;
  }

  return { setValue, setErrors, getValue, destroy, setVisible, setDisabled, clearValue, getOptionsProvider, getWrapper };
}
